// O LISTBOX DE ETIQUETAS DO ENVIO AO CAIXA — a coluna do MEIO ([DA-723]).
//
// Pedido dele em 12/09/2026, com print do modal: *"vamos criar uma coluna no meio das duas so'
// para exibir um Listbox com as Etiquetas, dessa forma creio que vai ficar mais amigavel para o
// usuario"*. Substitui o `<ComboEtiquetas>` dentro do caminho 2 — no campo fechado a lista custava
// um clique pra abrir e um painel `fixed` por cima do modal; aqui ela esta sempre a vista.
//
// ⛔ **LISTBOX, e a palavra e' dele.** Nao e' combo (nao abre), nao e' grade de chips solta (a
// grade de 14 caiu em 2 colunas dentro do bloco e fez o miolo rolar 265px — [DA-722]). E' uma
// caixa de altura FIXA com a lista rolando por dentro: o que ela consome de altura nao depende de
// quantas etiquetas existem, entao a lista pode crescer sem o modal crescer junto.
//
// ⚠️ **A altura foi medida, nao escolhida** ([DA-704]): 438px de lista deixam 13 das 14 visiveis na
// janela dele (1536x740). O `clamp` contra `100vh` existe pro caso de janela mais baixa — sem ele
// um teto fixo faria o modal rolar, que e' o defeito que a [DA-697] ja consertou uma vez.
import { Lock } from 'lucide-react';
import { SeletorEtiquetas } from '../../modules/atendimento/componentes/SeletorEtiquetas';
import { corDoTexto } from '../../modules/atendimento/componentes/BadgeEtiqueta';
import type { Etiqueta } from '../../modules/atendimento/lib/etiquetas-context';
import { marcadasNaTela, travadasNaTela, type EstadoEtiquetas } from './etiquetas-do-balcao';

interface Props {
  /** So' as que podem ser alvo (`paraCampanha`), JA em ordem alfabetica. */
  etiquetas: Etiqueta[];
  estado: EstadoEtiquetas;
  onAlternar: (id: number) => void;
  ehAdmin: boolean;
  desabilitado?: boolean;
  /**
   * O caminho 2 esta com o numero pronto e nenhuma etiqueta marcada ([DA-722] p.10).
   *
   * ⚠️ So' acende DEPOIS que a exigencia existe de verdade — antes do telefone, e nos caminhos 1 e
   * 3, nao ha o que exigir, e um `*` fixo afirmaria uma obrigacao que nao vale ([DA-649]).
   */
  pendente?: boolean;
  /**
   * Quem hospeda escreve o proprio aviso de cadeado. O Perfil do chat (26/10/2026) trava a lista
   * INTEIRA quando ja ha area gravada, e o aviso daqui ("da' pra acrescentar") mentiria la'.
   */
  semAvisoTravadas?: boolean;
}

export function ListboxEtiquetasVenda({
  etiquetas, estado, onAlternar, ehAdmin, desabilitado, pendente, semAvisoTravadas,
}: Props) {
  const marcadas = marcadasNaTela(estado);
  const travadas = travadasNaTela(estado, ehAdmin);
  const temTravada = !semAvisoTravadas && travadas.some((id) => etiquetas.some((e) => e.id === id));

  if (etiquetas.length === 0) return null;

  return (
    <div
      className="flex flex-col rounded-lg overflow-hidden"
      style={{ border: '1px solid var(--input-border)', backgroundColor: 'var(--panel-bg)' }}
    >
      <div className="flex-none px-2.5 pt-2 pb-1.5" style={{ borderBottom: '1px solid var(--input-border)' }}>
        <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          Etiqueta {pendente && <span className="text-red-600">*</span>} — area de atuacao
        </div>
        {/* A instrucao vale por si; o que ela NAO pode e' virar exemplo copiavel de um valor da
            propria lista ([DA-608] p.3). */}
        <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
          {pendente
            ? 'escolha ao menos uma pra liberar o Gravar'
            : desabilitado && semAvisoTravadas ? 'ja preenchida' : 'marque quantas precisar'}
        </div>
      </div>

      {/* ⚠️ **A caixa tem altura, a LISTA rola.** O `min-h-0` e' obrigatorio: sem ele o item de
          flex nunca encolhe abaixo do conteudo e o `overflow` nao chega a existir. */}
      <div
        className="min-h-0 overflow-y-auto px-2 py-1.5"
        style={{ height: 'clamp(180px, calc(100vh - 302px), 438px)' }}
      >
        <SeletorEtiquetas
          etiquetas={etiquetas}
          marcadas={marcadas}
          travadas={travadas}
          onAlternar={onAlternar}
          desabilitado={desabilitado}
          umaColuna
        />
      </div>

      {/* ⛔ **TODAS as marcadas, sempre** (pedido dele em 12/09: *"quando o contato tem mais de uma
          Etiqueta selecionada, deve exibir todas"*). No combo isso morava no campo fechado; aqui a
          lista ROLA, entao uma marcada fora da janela de rolagem ficaria invisivel — este rodape e'
          o que responde "o que esta marcado agora" sem obrigar a rolar.
          ⚠️ Ele come altura da LISTA, nunca do modal: a caixa inteira tem altura fixa. */}
      {marcadas.length > 0 && (
        <div
          className="flex-none px-2 py-1.5 flex flex-wrap gap-1"
          style={{ borderTop: '1px solid var(--input-border)' }}
        >
          {marcadas.map((id) => {
            const e = etiquetas.find((x) => x.id === id);
            if (!e) return null;
            const travada = travadas.includes(id);
            return (
              <span
                key={id}
                className="text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 whitespace-nowrap"
                style={{ backgroundColor: e.cor || '#e2e8f0', color: corDoTexto(e.cor || '#e2e8f0') }}
              >
                {travada && <Lock size={8} strokeWidth={3} />}
                {e.apelido || e.nome}
              </span>
            );
          })}
        </div>
      )}

      {/* O chip travado ja diz por que no `title`, mas tooltip exige mouse e o balcao e' tablet. */}
      {temTravada && (
        <div
          className="flex-none px-2 py-1.5 text-[10px] flex items-start gap-1.5"
          style={{ borderTop: '1px solid var(--input-border)', color: 'var(--text-muted)' }}
        >
          <Lock size={10} className="flex-shrink-0 mt-[2px]" />
          <span>Com cadeado ja estava gravada. Da' pra acrescentar; so' um administrador retira.</span>
        </div>
      )}
    </div>
  );
}
